import React, { useState, useRef, useMemo, useCallback } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { agrogestao } from "@/api/agrogestaoClient";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogDescription } from "@/components/ui/dialog";
import { Progress } from "@/components/ui/progress";
import {
    Loader2, FileCode2, Upload, Check, X, Link2, Package, AlertTriangle,
    ChevronRight, ChevronDown, Building2, Calendar, Hash, Trash2, RefreshCw, Sparkles
} from "lucide-react";

const categorias = [
    { value: "semente", label: "Sementes", keywords: ["semente", "seed"] },
    { value: "fertilizante", label: "Fertilizantes", keywords: ["fertilizante", "adubo", "npk", "ureia", "calcario", "cal", "nitrogênio", "fosforo", "potassio", "kcl", "map", "superfosfato"] },
    { value: "defensivo", label: "Defensivos", keywords: ["herbicida", "inseticida", "fungicida", "defensivo", "glifosato", "roundup", "atrazina", "2,4-d"] },
    { value: "combustivel", label: "Combustível", keywords: ["diesel", "gasolina", "etanol", "combustivel", "oleo lubrificante", "graxa"] },
    { value: "racao", label: "Ração", keywords: ["racao", "ração", "concentrado", "premix", "sal mineral", "suplemento"] },
    { value: "medicamento", label: "Medicamentos", keywords: ["vacina", "medicamento", "antibiotico", "vermifugo", "anti-inflamatorio", "remedio"] },
    { value: "outro", label: "Outros", keywords: [] }
];

function inferCategory(productName) {
    const lower = (productName || "").toLowerCase().normalize("NFD").replace(/[\u0300-\u036f]/g, "");
    for (const cat of categorias) {
        if (cat.keywords.some(kw => lower.includes(kw.normalize("NFD").replace(/[\u0300-\u036f]/g, "")))) {
            return cat.value;
        }
    }
    return "outro";
}

function normalizeText(text) {
    return (text || "").toLowerCase().normalize("NFD").replace(/[\u0300-\u036f]/g, "").replace(/\s+/g, " ").trim();
}

function matchScore(xmlName, insumoName) {
    const a = normalizeText(xmlName);
    const b = normalizeText(insumoName);
    if (a === b) return 100;
    if (a.includes(b) || b.includes(a)) return 80;
    const wordsA = a.split(" ").filter(w => w.length > 2);
    const wordsB = b.split(" ").filter(w => w.length > 2);
    if (wordsA.length === 0 || wordsB.length === 0) return 0;
    const matched = wordsA.filter(w => wordsB.some(wb => wb.includes(w) || w.includes(wb)));
    return Math.round((matched.length / Math.max(wordsA.length, wordsB.length)) * 100);
}

function parseNFeXML(xmlText) {
    const parser = new DOMParser();
    const doc = parser.parseFromString(xmlText, "text/xml");

    const parseError = doc.querySelector("parsererror");
    if (parseError) throw new Error("XML inválido. Verifique se o arquivo é uma NF-e válida.");

    // Helper to get text from element, handling namespaces
    const getText = (parent, tag) => {
        const el = parent.getElementsByTagName(tag)[0];
        return el?.textContent?.trim() || "";
    };

    // NF-e info
    const ide = doc.getElementsByTagName("ide")[0];
    const emit = doc.getElementsByTagName("emit")[0];
    const total = doc.getElementsByTagName("ICMSTot")[0];

    const nfInfo = {
        numero: getText(ide || doc, "nNF"),
        serie: getText(ide || doc, "serie"),
        data_emissao: getText(ide || doc, "dhEmi")?.substring(0, 10) || getText(ide || doc, "dEmi"),
        chave_acesso: "",
        emitente: {
            cnpj: getText(emit || doc, "CNPJ"),
            razao_social: getText(emit || doc, "xNome"),
            fantasia: getText(emit || doc, "xFant"),
            uf: getText(emit?.getElementsByTagName("enderEmit")[0] || doc, "UF"),
            municipio: getText(emit?.getElementsByTagName("enderEmit")[0] || doc, "xMun"),
        },
        valor_total: parseFloat(getText(total || doc, "vNF")) || 0,
        valor_produtos: parseFloat(getText(total || doc, "vProd")) || 0,
        valor_frete: parseFloat(getText(total || doc, "vFrete")) || 0,
        valor_desconto: parseFloat(getText(total || doc, "vDesc")) || 0,
    };

    // Try chave de acesso
    const infNFe = doc.getElementsByTagName("infNFe")[0];
    if (infNFe) {
        const id = infNFe.getAttribute("Id") || "";
        nfInfo.chave_acesso = id.replace("NFe", "");
    }

    // Products
    const dets = doc.getElementsByTagName("det");
    const produtos = [];

    for (const det of dets) {
        const prod = det.getElementsByTagName("prod")[0];
        if (!prod) continue;

        const nome = getText(prod, "xProd");
        const ncm = getText(prod, "NCM");
        const cfop = getText(prod, "CFOP");
        const unidade = getText(prod, "uCom") || getText(prod, "uTrib");
        const quantidade = parseFloat(getText(prod, "qCom") || getText(prod, "qTrib")) || 0;
        const valorUnitario = parseFloat(getText(prod, "vUnCom") || getText(prod, "vUnTrib")) || 0;
        const valorTotal = parseFloat(getText(prod, "vProd")) || 0;
        const codigo = getText(prod, "cProd");
        const ean = getText(prod, "cEAN");

        produtos.push({
            id: `xml_${Date.now()}_${Math.random().toString(36).substr(2, 5)}`,
            nome,
            codigo,
            ean: ean !== "SEM GTIN" ? ean : "",
            ncm,
            cfop,
            unidade: unidade.toLowerCase(),
            quantidade,
            valorUnitario,
            valorTotal: valorTotal || quantidade * valorUnitario,
            categoriaInferida: inferCategory(nome),
            // Matching
            vinculado: null, // insumo_id linked
            vinculoTipo: "novo", // "novo" | "existente"
            matchSugestoes: [],
            collapsed: false,
        });
    }

    return { nfInfo, produtos };
}

export default function ImportacaoXML({ insumos, propriedadeId }) {
    const [dialogOpen, setDialogOpen] = useState(false);
    const [step, setStep] = useState(1); // 1: upload, 2: review/vincular, 3: processing
    const [nfInfo, setNfInfo] = useState(null);
    const [produtos, setProdutos] = useState([]);
    const [processProgress, setProcessProgress] = useState(0);
    const [processLog, setProcessLog] = useState([]);
    const [error, setError] = useState(null);
    const fileInputRef = useRef(null);
    const queryClient = useQueryClient();

    const totalVinculados = useMemo(() => {
        return produtos.filter(p => p.vinculoTipo === "existente" && p.vinculado).length;
    }, [produtos]);

    const totalNovos = useMemo(() => {
        return produtos.filter(p => p.vinculoTipo === "novo").length;
    }, [produtos]);

    // Auto-match products
    const autoMatch = useCallback((xmlProducts, existingInsumos) => {
        return xmlProducts.map(prod => {
            const scores = existingInsumos.map(ins => ({
                insumo: ins,
                score: matchScore(prod.nome, ins.nome)
            })).filter(s => s.score >= 50).sort((a, b) => b.score - a.score);

            const bestMatch = scores[0];
            return {
                ...prod,
                matchSugestoes: scores.slice(0, 5),
                vinculado: bestMatch?.score >= 80 ? bestMatch.insumo.id : null,
                vinculoTipo: bestMatch?.score >= 80 ? "existente" : "novo",
            };
        });
    }, []);

    const handleFileUpload = async (e) => {
        const file = e.target.files?.[0];
        if (!file) return;
        setError(null);

        try {
            const text = await file.text();
            const { nfInfo: info, produtos: prods } = parseNFeXML(text);

            if (prods.length === 0) {
                setError("Nenhum produto encontrado no XML. Verifique se é uma NF-e válida.");
                return;
            }

            const matched = autoMatch(prods, insumos);
            setNfInfo(info);
            setProdutos(matched);
            setStep(2);
        } catch (err) {
            setError(err.message || "Erro ao processar XML");
        }

        if (fileInputRef.current) fileInputRef.current.value = "";
    };

    const toggleVinculoTipo = (prodId) => {
        setProdutos(prev => prev.map(p =>
            p.id === prodId
                ? { ...p, vinculoTipo: p.vinculoTipo === "novo" ? "existente" : "novo", vinculado: null }
                : p
        ));
    };

    const setVinculo = (prodId, insumoId) => {
        setProdutos(prev => prev.map(p =>
            p.id === prodId ? { ...p, vinculado: insumoId, vinculoTipo: "existente" } : p
        ));
    };

    const removeProduto = (prodId) => {
        setProdutos(prev => prev.filter(p => p.id !== prodId));
    };

    const toggleCollapsed = (prodId) => {
        setProdutos(prev => prev.map(p =>
            p.id === prodId ? { ...p, collapsed: !p.collapsed } : p
        ));
    };

    const autoMatchAll = () => {
        setProdutos(prev => autoMatch(prev, insumos));
    };

    // Process import
    const processImport = async () => {
        setStep(3);
        setProcessProgress(0);
        setProcessLog([]);
        const total = produtos.length;
        const user = await agrogestao.auth.me();
        const dataHoje = new Date().toISOString().split("T")[0];

        for (let i = 0; i < produtos.length; i++) {
            const prod = produtos[i];
            const progress = Math.round(((i + 1) / total) * 100);

            try {
                if (prod.vinculoTipo === "existente" && prod.vinculado) {
                    // Update existing
                    const existing = insumos.find(ins => ins.id === prod.vinculado);
                    if (!existing) throw new Error("Insumo vinculado não encontrado");

                    const qtdAnterior = existing.quantidade_estoque || 0;
                    const custoAnterior = existing.custo_unitario || 0;
                    const novaQtd = qtdAnterior + prod.quantidade;
                    const custoMedio = novaQtd > 0
                        ? ((qtdAnterior * custoAnterior) + prod.valorTotal) / novaQtd
                        : prod.valorUnitario;

                    await agrogestao.entities.Insumo.update(existing.id, {
                        quantidade_estoque: novaQtd,
                        custo_unitario: Math.round(custoMedio * 100) / 100,
                        fornecedor: nfInfo.emitente.razao_social || existing.fornecedor,
                        nota_fiscal: nfInfo.numero || existing.nota_fiscal,
                    });

                    await agrogestao.entities.MovimentacaoInsumo.create({
                        propriedade_id: propriedadeId,
                        insumo_id: existing.id,
                        tipo: "entrada",
                        quantidade: prod.quantidade,
                        data: dataHoje,
                        custo_total: prod.valorTotal,
                        observacao: `Importação XML NF ${nfInfo.numero} - ${prod.nome}`
                    });

                    setProcessLog(prev => [...prev, { type: "update", name: prod.nome, linked: existing.nome }]);
                } else {
                    // Create new
                    const novoInsumo = await agrogestao.entities.Insumo.create({
                        propriedade_id: propriedadeId,
                        nome: prod.nome,
                        categoria: prod.categoriaInferida,
                        unidade: prod.unidade || "un",
                        quantidade_estoque: prod.quantidade,
                        custo_unitario: prod.valorUnitario,
                        fornecedor: nfInfo.emitente.razao_social || null,
                        nota_fiscal: nfInfo.numero || null,
                        created_by: user?.email || null,
                    });

                    await agrogestao.entities.MovimentacaoInsumo.create({
                        propriedade_id: propriedadeId,
                        insumo_id: novoInsumo.id,
                        tipo: "entrada",
                        quantidade: prod.quantidade,
                        data: dataHoje,
                        custo_total: prod.valorTotal,
                        observacao: `Importação XML NF ${nfInfo.numero} - Novo cadastro`
                    });

                    setProcessLog(prev => [...prev, { type: "create", name: prod.nome }]);
                }

                // Create Conta a Pagar
                await agrogestao.entities.ContaPagar.create({
                    propriedade_id: propriedadeId,
                    descricao: `NF ${nfInfo.numero}: ${prod.nome} (${prod.quantidade} ${prod.unidade})`,
                    valor: prod.valorTotal,
                    data_vencimento: nfInfo.data_emissao || dataHoje,
                    categoria: "insumos",
                    fornecedor: nfInfo.emitente.razao_social || "",
                    status: "pendente",
                });

            } catch (err) {
                setProcessLog(prev => [...prev, { type: "error", name: prod.nome, error: err.message }]);
            }

            setProcessProgress(progress);
        }

        queryClient.invalidateQueries({ queryKey: ["insumos"] });
        queryClient.invalidateQueries({ queryKey: ["movimentacoes"] });
        queryClient.invalidateQueries({ queryKey: ["contas_pagar"] });
    };

    const resetAll = () => {
        setStep(1);
        setNfInfo(null);
        setProdutos([]);
        setProcessProgress(0);
        setProcessLog([]);
        setError(null);
    };

    const formatCurrency = (v) => new Intl.NumberFormat("pt-BR", { style: "currency", currency: "BRL" }).format(v || 0);

    return (
        <Dialog open={dialogOpen} onOpenChange={(open) => { setDialogOpen(open); if (!open) resetAll(); }}>
            <DialogTrigger asChild>
                <Button className="bg-emerald-600 hover:bg-emerald-700 gap-2">
                    <FileCode2 className="w-5 h-5" />
                    Importar XML
                </Button>
            </DialogTrigger>

            <DialogContent className="sm:max-w-3xl max-h-[92vh] overflow-y-auto p-0">
                {/* Header com steps */}
                <div className="sticky top-0 z-10 bg-white border-b border-stone-200 px-6 pt-6 pb-4">
                    <DialogHeader>
                        <DialogTitle className="flex items-center gap-2 text-lg">
                            <FileCode2 className="w-5 h-5 text-emerald-600" />
                            Importação de XML (NF-e)
                        </DialogTitle>
                        <DialogDescription>
                            Importe produtos automaticamente a partir do XML da nota fiscal eletrônica
                        </DialogDescription>
                    </DialogHeader>

                    {/* Progress Steps */}
                    <div className="flex items-center gap-2 mt-4">
                        {[
                            { n: 1, label: "Upload" },
                            { n: 2, label: "Revisão" },
                            { n: 3, label: "Processamento" },
                        ].map((s, idx) => (
                            <React.Fragment key={s.n}>
                                {idx > 0 && <div className={`flex-1 h-0.5 ${step >= s.n ? "bg-emerald-500" : "bg-stone-200"}`} />}
                                <div className={`flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-medium transition-all ${step === s.n ? "bg-emerald-100 text-emerald-700" :
                                        step > s.n ? "bg-emerald-500 text-white" : "bg-stone-100 text-stone-400"
                                    }`}>
                                    {step > s.n ? <Check className="w-3 h-3" /> : <span>{s.n}</span>}
                                    {s.label}
                                </div>
                            </React.Fragment>
                        ))}
                    </div>
                </div>

                <div className="px-6 pb-6 pt-4">
                    {/* STEP 1: Upload */}
                    {step === 1 && (
                        <div className="space-y-6">
                            {error && (
                                <div className="flex items-start gap-3 p-4 bg-red-50 border border-red-200 rounded-xl text-sm text-red-700">
                                    <AlertTriangle className="w-5 h-5 mt-0.5 shrink-0" />
                                    <div>
                                        <p className="font-semibold">Erro ao processar XML</p>
                                        <p>{error}</p>
                                    </div>
                                </div>
                            )}

                            <input
                                ref={fileInputRef}
                                type="file"
                                accept=".xml"
                                onChange={handleFileUpload}
                                className="hidden"
                            />

                            <div
                                onClick={() => fileInputRef.current?.click()}
                                className="border-2 border-dashed border-stone-300 hover:border-emerald-400 rounded-2xl p-12 text-center cursor-pointer transition-all hover:bg-emerald-50/50 group"
                            >
                                <div className="w-16 h-16 rounded-2xl bg-emerald-100 flex items-center justify-center mx-auto mb-4 group-hover:scale-110 transition-transform">
                                    <Upload className="w-8 h-8 text-emerald-600" />
                                </div>
                                <p className="text-lg font-semibold text-stone-700">Arraste ou clique para selecionar</p>
                                <p className="text-sm text-stone-500 mt-1">Arquivo XML da Nota Fiscal Eletrônica (NF-e)</p>
                                <div className="flex items-center justify-center gap-4 mt-6">
                                    <Badge variant="outline" className="gap-1">
                                        <FileCode2 className="w-3 h-3" /> Formato .xml
                                    </Badge>
                                    <Badge variant="outline" className="gap-1">
                                        <Sparkles className="w-3 h-3" /> Detecção automática
                                    </Badge>
                                </div>
                            </div>

                            <Card className="bg-stone-50 border-stone-200">
                                <CardContent className="p-4">
                                    <h4 className="font-semibold text-stone-700 text-sm mb-3">Como funciona?</h4>
                                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 text-xs text-stone-600">
                                        <div className="flex gap-2">
                                            <div className="w-6 h-6 rounded-full bg-emerald-100 text-emerald-700 flex items-center justify-center shrink-0 text-[10px] font-bold">1</div>
                                            <p>Faça upload do XML da NF-e emitida pelo fornecedor</p>
                                        </div>
                                        <div className="flex gap-2">
                                            <div className="w-6 h-6 rounded-full bg-emerald-100 text-emerald-700 flex items-center justify-center shrink-0 text-[10px] font-bold">2</div>
                                            <p>Revise os produtos e vincule com insumos já cadastrados ou crie novos</p>
                                        </div>
                                        <div className="flex gap-2">
                                            <div className="w-6 h-6 rounded-full bg-emerald-100 text-emerald-700 flex items-center justify-center shrink-0 text-[10px] font-bold">3</div>
                                            <p>Confirme para atualizar o estoque e gerar contas a pagar</p>
                                        </div>
                                    </div>
                                </CardContent>
                            </Card>
                        </div>
                    )}

                    {/* STEP 2: Review & Match */}
                    {step === 2 && nfInfo && (
                        <div className="space-y-5">
                            {/* NF Summary */}
                            <Card className="bg-gradient-to-r from-emerald-50 to-teal-50 border-emerald-200">
                                <CardContent className="p-4">
                                    <div className="flex items-start justify-between">
                                        <div>
                                            <div className="flex items-center gap-2 mb-1">
                                                <Building2 className="w-4 h-4 text-emerald-600" />
                                                <span className="font-semibold text-stone-800">{nfInfo.emitente.razao_social || nfInfo.emitente.fantasia}</span>
                                            </div>
                                            <div className="flex flex-wrap gap-x-4 gap-y-1 text-xs text-stone-500">
                                                <span className="flex items-center gap-1">
                                                    <Hash className="w-3 h-3" /> NF {nfInfo.numero}{nfInfo.serie ? ` / Série ${nfInfo.serie}` : ""}
                                                </span>
                                                <span className="flex items-center gap-1">
                                                    <Calendar className="w-3 h-3" /> {nfInfo.data_emissao}
                                                </span>
                                                {nfInfo.emitente.cnpj && (
                                                    <span>CNPJ: {nfInfo.emitente.cnpj.replace(/(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})/, "$1.$2.$3/$4-$5")}</span>
                                                )}
                                            </div>
                                        </div>
                                        <div className="text-right">
                                            <p className="text-xs text-stone-500">Total NF</p>
                                            <p className="text-lg font-bold text-emerald-700">{formatCurrency(nfInfo.valor_total)}</p>
                                        </div>
                                    </div>
                                </CardContent>
                            </Card>

                            {/* Match summary badges */}
                            <div className="flex items-center justify-between">
                                <div className="flex gap-2">
                                    <Badge className="bg-blue-100 text-blue-700 gap-1">
                                        <Package className="w-3 h-3" /> {produtos.length} produtos
                                    </Badge>
                                    <Badge className="bg-green-100 text-green-700 gap-1">
                                        <Link2 className="w-3 h-3" /> {totalVinculados} vinculados
                                    </Badge>
                                    <Badge className="bg-amber-100 text-amber-700 gap-1">
                                        <Sparkles className="w-3 h-3" /> {totalNovos} novos
                                    </Badge>
                                </div>
                                <Button variant="outline" size="sm" onClick={autoMatchAll} className="gap-1 text-xs">
                                    <RefreshCw className="w-3.5 h-3.5" /> Re-vincular
                                </Button>
                            </div>

                            {/* Products list */}
                            <div className="space-y-3 max-h-[50vh] overflow-y-auto pr-1">
                                {produtos.map((prod, idx) => (
                                    <Card key={prod.id} className={`border transition-all ${prod.vinculoTipo === "existente" && prod.vinculado
                                            ? "border-green-200 bg-green-50/30"
                                            : "border-stone-200"
                                        }`}>
                                        <CardContent className="p-3">
                                            {/* Product header */}
                                            <div className="flex items-start gap-3">
                                                <button
                                                    onClick={() => toggleCollapsed(prod.id)}
                                                    className="mt-1 shrink-0 text-stone-400 hover:text-stone-600"
                                                >
                                                    {prod.collapsed ? <ChevronRight className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                                                </button>

                                                <div className="flex-1 min-w-0">
                                                    <div className="flex items-center justify-between gap-2">
                                                        <h4 className="font-semibold text-stone-800 text-sm truncate">{prod.nome}</h4>
                                                        <div className="flex items-center gap-2 shrink-0">
                                                            <span className="text-sm font-bold text-stone-700">{formatCurrency(prod.valorTotal)}</span>
                                                            <button onClick={() => removeProduto(prod.id)} className="text-stone-300 hover:text-red-500 transition-colors">
                                                                <Trash2 className="w-3.5 h-3.5" />
                                                            </button>
                                                        </div>
                                                    </div>
                                                    <div className="flex flex-wrap gap-x-3 gap-y-0.5 text-xs text-stone-500 mt-0.5">
                                                        <span>{prod.quantidade} {prod.unidade}</span>
                                                        <span>× {formatCurrency(prod.valorUnitario)}</span>
                                                        {prod.ncm && <span>NCM: {prod.ncm}</span>}
                                                        {prod.codigo && <span>Cód: {prod.codigo}</span>}
                                                    </div>
                                                </div>
                                            </div>

                                            {/* Expanded details */}
                                            {!prod.collapsed && (
                                                <div className="mt-3 ml-7 space-y-3">
                                                    {/* Toggle: novo vs existente */}
                                                    <div className="flex gap-1 p-0.5 bg-stone-100 rounded-lg w-fit">
                                                        <button
                                                            onClick={() => setProdutos(prev => prev.map(p => p.id === prod.id ? { ...p, vinculoTipo: "novo", vinculado: null } : p))}
                                                            className={`px-3 py-1 rounded-md text-xs font-medium transition-all ${prod.vinculoTipo === "novo" ? "bg-amber-500 text-white shadow-sm" : "text-stone-500"
                                                                }`}
                                                        >
                                                            Novo Insumo
                                                        </button>
                                                        <button
                                                            onClick={() => setProdutos(prev => prev.map(p => p.id === prod.id ? { ...p, vinculoTipo: "existente" } : p))}
                                                            className={`px-3 py-1 rounded-md text-xs font-medium transition-all ${prod.vinculoTipo === "existente" ? "bg-green-500 text-white shadow-sm" : "text-stone-500"
                                                                }`}
                                                        >
                                                            Vincular Existente
                                                        </button>
                                                    </div>

                                                    {prod.vinculoTipo === "novo" ? (
                                                        <div className="flex items-center gap-2 p-2 bg-amber-50 border border-amber-200 rounded-lg text-xs text-amber-700">
                                                            <Sparkles className="w-4 h-4" />
                                                            <span>
                                                                Será criado como <strong>{categorias.find(c => c.value === prod.categoriaInferida)?.label}</strong>
                                                            </span>
                                                            <Select
                                                                value={prod.categoriaInferida}
                                                                onValueChange={(v) => setProdutos(prev => prev.map(p => p.id === prod.id ? { ...p, categoriaInferida: v } : p))}
                                                            >
                                                                <SelectTrigger className="h-6 w-32 text-xs ml-auto">
                                                                    <SelectValue />
                                                                </SelectTrigger>
                                                                <SelectContent>
                                                                    {categorias.map(c => (
                                                                        <SelectItem key={c.value} value={c.value} className="text-xs">{c.label}</SelectItem>
                                                                    ))}
                                                                </SelectContent>
                                                            </Select>
                                                        </div>
                                                    ) : (
                                                        <div className="space-y-2">
                                                            <Select
                                                                value={prod.vinculado || ""}
                                                                onValueChange={(v) => setVinculo(prod.id, v)}
                                                            >
                                                                <SelectTrigger className={`h-9 text-xs ${prod.vinculado ? "border-green-300 bg-green-50" : "border-amber-300"}`}>
                                                                    <SelectValue placeholder="Selecione um insumo cadastrado..." />
                                                                </SelectTrigger>
                                                                <SelectContent>
                                                                    {/* Sugestões primeiro */}
                                                                    {prod.matchSugestoes.length > 0 && (
                                                                        <>
                                                                            <div className="px-2 py-1 text-[10px] font-semibold text-emerald-600 uppercase tracking-wider">
                                                                                Sugestões ({prod.matchSugestoes.length})
                                                                            </div>
                                                                            {prod.matchSugestoes.map(s => (
                                                                                <SelectItem key={`sug_${s.insumo.id}`} value={s.insumo.id} className="text-xs">
                                                                                    <div className="flex items-center gap-2">
                                                                                        <Badge className="bg-emerald-100 text-emerald-700 text-[10px] px-1">{s.score}%</Badge>
                                                                                        {s.insumo.nome}
                                                                                        <span className="text-stone-400 ml-auto">({s.insumo.quantidade_estoque} {s.insumo.unidade})</span>
                                                                                    </div>
                                                                                </SelectItem>
                                                                            ))}
                                                                            <div className="px-2 py-1 text-[10px] font-semibold text-stone-400 uppercase tracking-wider border-t mt-1 pt-1">
                                                                                Todos os insumos
                                                                            </div>
                                                                        </>
                                                                    )}
                                                                    {insumos.map(ins => (
                                                                        <SelectItem key={ins.id} value={ins.id} className="text-xs">
                                                                            {ins.nome} ({ins.quantidade_estoque} {ins.unidade})
                                                                        </SelectItem>
                                                                    ))}
                                                                </SelectContent>
                                                            </Select>

                                                            {prod.vinculado && (() => {
                                                                const linked = insumos.find(i => i.id === prod.vinculado);
                                                                if (!linked) return null;
                                                                return (
                                                                    <div className="flex items-center gap-2 p-2 bg-green-50 border border-green-200 rounded-lg text-xs text-green-700">
                                                                        <Check className="w-4 h-4" />
                                                                        <span>
                                                                            Estoque atual: <strong>{linked.quantidade_estoque} {linked.unidade}</strong> → Novo: <strong>{linked.quantidade_estoque + prod.quantidade} {linked.unidade}</strong>
                                                                        </span>
                                                                    </div>
                                                                );
                                                            })()}
                                                        </div>
                                                    )}
                                                </div>
                                            )}
                                        </CardContent>
                                    </Card>
                                ))}
                            </div>

                            {/* Actions */}
                            <div className="flex gap-3 pt-2 border-t border-stone-100">
                                <Button variant="outline" onClick={resetAll} className="flex-1">
                                    Cancelar
                                </Button>
                                <Button
                                    onClick={processImport}
                                    disabled={produtos.some(p => p.vinculoTipo === "existente" && !p.vinculado)}
                                    className="flex-1 bg-emerald-600 hover:bg-emerald-700 gap-2"
                                >
                                    <Check className="w-4 h-4" />
                                    Importar {produtos.length} {produtos.length === 1 ? "produto" : "produtos"}
                                </Button>
                            </div>
                            {produtos.some(p => p.vinculoTipo === "existente" && !p.vinculado) && (
                                <p className="text-xs text-amber-600 text-center">
                                    ⚠ Todos os itens marcados como "Vincular Existente" precisam ter um insumo selecionado
                                </p>
                            )}
                        </div>
                    )}

                    {/* STEP 3: Processing */}
                    {step === 3 && (
                        <div className="space-y-6">
                            <div className="text-center space-y-4">
                                {processProgress < 100 ? (
                                    <>
                                        <div className="w-16 h-16 rounded-2xl bg-emerald-100 flex items-center justify-center mx-auto">
                                            <Loader2 className="w-8 h-8 text-emerald-600 animate-spin" />
                                        </div>
                                        <div>
                                            <p className="font-semibold text-stone-800">Processando importação...</p>
                                            <p className="text-sm text-stone-500">
                                                {processLog.length} de {produtos.length} produtos processados
                                            </p>
                                        </div>
                                    </>
                                ) : (
                                    <>
                                        <div className="w-16 h-16 rounded-2xl bg-emerald-100 flex items-center justify-center mx-auto">
                                            <Check className="w-8 h-8 text-emerald-600" />
                                        </div>
                                        <div>
                                            <p className="text-lg font-bold text-stone-800">Importação Concluída!</p>
                                            <p className="text-sm text-stone-500">
                                                {processLog.filter(l => l.type !== "error").length} de {produtos.length} produtos importados com sucesso
                                            </p>
                                        </div>
                                    </>
                                )}
                            </div>

                            <Progress value={processProgress} className="h-3 [&>div]:bg-emerald-500" />

                            {/* Log */}
                            <div className="bg-stone-50 rounded-xl border border-stone-200 max-h-60 overflow-y-auto">
                                <div className="p-3 border-b border-stone-200">
                                    <p className="text-xs font-semibold text-stone-600 uppercase tracking-wider">Log de Processamento</p>
                                </div>
                                <div className="divide-y divide-stone-100">
                                    {processLog.map((log, i) => (
                                        <div key={i} className="px-3 py-2 flex items-center gap-2 text-xs">
                                            {log.type === "create" && (
                                                <>
                                                    <Badge className="bg-amber-100 text-amber-700 text-[10px]">NOVO</Badge>
                                                    <span className="text-stone-700">{log.name}</span>
                                                </>
                                            )}
                                            {log.type === "update" && (
                                                <>
                                                    <Badge className="bg-green-100 text-green-700 text-[10px]">ATUALIZADO</Badge>
                                                    <span className="text-stone-700">{log.name}</span>
                                                    <span className="text-stone-400">→ {log.linked}</span>
                                                </>
                                            )}
                                            {log.type === "error" && (
                                                <>
                                                    <Badge className="bg-red-100 text-red-700 text-[10px]">ERRO</Badge>
                                                    <span className="text-red-600">{log.name}: {log.error}</span>
                                                </>
                                            )}
                                        </div>
                                    ))}
                                </div>
                            </div>

                            {processProgress >= 100 && (
                                <Button onClick={() => { resetAll(); setDialogOpen(false); }} className="w-full bg-emerald-600 hover:bg-emerald-700 gap-2">
                                    <Check className="w-4 h-4" /> Fechar
                                </Button>
                            )}
                        </div>
                    )}
                </div>
            </DialogContent>
        </Dialog>
    );
}
